<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useQaPermissions } from 'dashboard/composables/useQaPermissions';
import { confirmQaDestructive } from 'dashboard/composables/useQaConfirm';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import DropdownContainer from 'dashboard/components-next/dropdown-menu/base/DropdownContainer.vue';

// QA_FE_CONFIRM_V1 + QA_FE_SAFETY_I18N_V1 + QA_FE_LAYOUT_CLEAN_V1
const { t } = useI18n();
const toArray = value => (Array.isArray(value) ? value : []);
const showAlert = useAlert;
const { accountScopedRoute } = useAccount();
const { canReview, canManagePolicies } = useQaPermissions();

const pageSection = ref('review');
const pageSections = [
  { id: 'review', label: 'Review' },
  { id: 'settings', label: 'Settings' },
  { id: 'lexicon', label: 'Lexicon' },
  { id: 'quality', label: 'Quality' },
];

const isSettingsLoading = ref(false);
const isSettingsSaving = ref(false);
const isLexiconLoading = ref(false);
const isIncidentsLoading = ref(false);
const isFeedbackLoading = ref(false);
const isApplyingRecommendation = ref(false);
const isPreviewLoading = ref(false);

const defaultLexicon = ref([]);
const lexiconEntries = ref([]);
const incidents = ref([]);

const settingsForm = reactive({
  enabled: false,
  useDefaultLexicon: true,
  aiEnabled: false,
  aiModel: 'omni-moderation-latest',
  aiMinScore: 0.75,
  contextualAiEnabled: false,
  contextualAiModel: 'gpt-4.1-nano',
  moderationDailyCap: 500,
  contextualDailyCap: 100,
  allowlistPhrasesText: '',
  allowlistRegexesText: '',
  settingsVersion: 0,
  telegramEnabled: false,
  workforceInactivitySeconds: 900,
  slowReplySeconds: 600,
  destinations: [],
  routes: [],
});

const newEntry = reactive({
  phrase: '',
  language: 'mixed',
  severity: 'medium',
  category: 'personal_insult',
  matchType: 'contains',
  note: '',
});

const lexiconFilters = reactive({
  q: '',
  language: 'all',
  category: 'all',
  lifecycle_status: 'all',
  scope: 'all',
});

const taxonomyCatalog = ref([]);
const supportedLanguages = ref([
  'ms',
  'en',
  'zh',
  'ta',
  'mixed',
  'romanized_zh',
  'romanized_ta',
]);
const packLedger = ref(null);
const seedVersion = ref('');
const licenseSource = ref('');

const taxonomyOptions = computed(() => {
  if (taxonomyCatalog.value.length) {
    return taxonomyCatalog.value.map(row => row.key || row);
  }
  return [
    'rudeness',
    'profanity',
    'personal_insult',
    'mockery_or_sarcasm',
    'harassment',
    'protected_class_hate',
    'sexual_harassment',
    'threat',
  ];
});

const languageOptions = [
  { value: 'ms', label: 'Malay' },
  { value: 'zh', label: 'Chinese' },
  { value: 'en', label: 'English' },
  { value: 'ta', label: 'Tamil' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'romanized_zh', label: 'Romanized Chinese' },
  { value: 'romanized_ta', label: 'Romanized Tamil' },
];

const incidentFilters = reactive({
  status: 'all',
  severity: 'all',
  detectorSource: 'all',
  page: 1,
  perPage: 25,
});

const incidentMeta = reactive({
  totalCount: 0,
  totalPages: 1,
});

const feedbackSummary = reactive({
  reviewedTotal: 0,
  confirmedCount: 0,
  passedCount: 0,
  precisionRatio: 0,
  falsePositiveRatio: 0,
  topFalsePositiveTerms: [],
  topConfirmedTerms: [],
  lexiconRecommendations: [],
});

const recommendationPreview = ref([]);
const selectedPreviewIds = ref([]);

const SEVERITY_RANK = {
  low: 0,
  medium: 1,
  high: 2,
  critical: 3,
};

const incidentStats = reactive({
  total: 0,
  pending: 0,
  confirmed: 0,
});

const splitCsv = value =>
  String(value || '')
    .split(',')
    .map(item => item.trim())
    .filter(Boolean);

const nextId = prefix =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const fillSettingsForm = item => {
  const settings = item || {};
  const telegram = settings.telegram_alerts || {};

  settingsForm.enabled = Boolean(settings.enabled);
  settingsForm.useDefaultLexicon = settings.use_default_lexicon !== false;
  settingsForm.aiEnabled = Boolean(settings.ai_enabled);
  settingsForm.aiModel = settings.ai_model || 'omni-moderation-latest';
  settingsForm.aiMinScore = Number(settings.ai_min_score || 0.75);
  settingsForm.contextualAiEnabled = Boolean(settings.contextual_ai_enabled);
  settingsForm.contextualAiModel =
    settings.contextual_ai_model || 'gpt-4.1-nano';
  settingsForm.moderationDailyCap = Number(
    settings.moderation_daily_cap || 500
  );
  settingsForm.contextualDailyCap = Number(
    settings.contextual_daily_cap || 100
  );
  settingsForm.allowlistPhrasesText = toArray(
    settings.allowlist_phrases || []
  ).join(', ');
  settingsForm.allowlistRegexesText = toArray(
    settings.allowlist_regexes || []
  ).join(', ');
  settingsForm.settingsVersion = Number(settings.settings_version || 0);

  settingsForm.telegramEnabled = Boolean(telegram.enabled);
  settingsForm.workforceInactivitySeconds = Number(
    telegram.workforce_inactivity_seconds || 900
  );
  settingsForm.slowReplySeconds = Number(telegram.slow_reply_seconds || 600);

  settingsForm.destinations = toArray(telegram.destinations || []).map(row => ({
    id: row.id || nextId('dest'),
    name: row.name || '',
    chatId: row.chat_id || '',
    messageThreadId: row.message_thread_id || '',
    enabled: row.enabled !== false,
  }));

  settingsForm.routes = toArray(telegram.routes || []).map(row => ({
    id: row.id || nextId('route'),
    incidentType: row.incident_type || '',
    statusesText: toArray(row.statuses || []).join(', '),
    severitiesText: toArray(row.severities || []).join(', '),
    destinationIdsText: toArray(row.destination_ids || []).join(', '),
    conditions: row.conditions || {},
    enabled: row.enabled !== false,
  }));
};

const settingsPayload = () => ({
  enabled: settingsForm.enabled,
  use_default_lexicon: settingsForm.useDefaultLexicon,
  ai_enabled: settingsForm.aiEnabled,
  ai_model: settingsForm.aiModel,
  ai_min_score: Number(settingsForm.aiMinScore) || 0.75,
  contextual_ai_enabled: settingsForm.contextualAiEnabled,
  contextual_ai_model: settingsForm.contextualAiModel,
  moderation_daily_cap: Number(settingsForm.moderationDailyCap) || 500,
  contextual_daily_cap: Number(settingsForm.contextualDailyCap) || 100,
  allowlist_phrases: splitCsv(settingsForm.allowlistPhrasesText),
  allowlist_regexes: splitCsv(settingsForm.allowlistRegexesText),
  settings_version: settingsForm.settingsVersion,
});

const loadSafetySettings = async () => {
  isSettingsLoading.value = true;
  try {
    const response = await QaDashboardApi.getSafetySettings();
    fillSettingsForm(response?.data?.item || {});
    defaultLexicon.value = response?.data?.default_lexicon || [];
    taxonomyCatalog.value = response?.data?.taxonomy || [];
    if (
      Array.isArray(response?.data?.supported_languages) &&
      response.data.supported_languages.length
    ) {
      supportedLanguages.value = response.data.supported_languages;
    }
    packLedger.value = response?.data?.language_pack_ledger || null;
    seedVersion.value = response?.data?.seed_version || '';
    licenseSource.value = response?.data?.license_source || '';
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_LOAD_SETTINGS')
    );
  } finally {
    isSettingsLoading.value = false;
  }
};

const saveSafetySettings = async () => {
  isSettingsSaving.value = true;
  try {
    await QaDashboardApi.updateSafetySettings(settingsPayload());
    await loadSafetySettings();
    showAlert(t('QA_DASHBOARD.SAFETY.SUCCESS_SETTINGS_SAVED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_SAVE_SETTINGS')
    );
  } finally {
    isSettingsSaving.value = false;
  }
};

const loadLexiconEntries = async () => {
  isLexiconLoading.value = true;
  try {
    const params = {};
    if (lexiconFilters.q.trim()) params.q = lexiconFilters.q.trim();
    if (lexiconFilters.language !== 'all')
      params.language = lexiconFilters.language;
    if (lexiconFilters.category !== 'all')
      params.category = lexiconFilters.category;
    if (lexiconFilters.lifecycle_status !== 'all') {
      params.lifecycle_status = lexiconFilters.lifecycle_status;
    }
    if (lexiconFilters.scope !== 'all') params.scope = lexiconFilters.scope;
    const response = await QaDashboardApi.getSafetyLexiconEntries(params);
    lexiconEntries.value = response?.data?.items || [];
    if (Array.isArray(response?.data?.defaults)) {
      defaultLexicon.value = response.data.defaults;
    }
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_LOAD_LEXICON')
    );
  } finally {
    isLexiconLoading.value = false;
  }
};

const createLexiconEntry = async () => {
  if (!newEntry.phrase.trim()) {
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_PHRASE_REQUIRED'));
    return;
  }
  try {
    await QaDashboardApi.createSafetyLexiconEntry({
      phrase: newEntry.phrase.trim(),
      language: newEntry.language,
      severity: newEntry.severity,
      category: newEntry.category.trim(),
      match_type: newEntry.matchType,
      note: newEntry.note.trim(),
      enabled: false,
    });
    newEntry.phrase = '';
    newEntry.note = '';
    await loadLexiconEntries();
    showAlert(t('QA_DASHBOARD.SAFETY.SUCCESS_DRAFT_CREATED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_CREATE_LEXICON')
    );
  }
};

const updateLexiconEntry = async entry => {
  try {
    await QaDashboardApi.updateSafetyLexiconEntry(entry.id, {
      phrase: entry.phrase,
      language: entry.language,
      severity: entry.severity,
      category: entry.category,
      match_type: entry.match_type,
      note: entry.note || '',
      enabled: entry.enabled,
    });
    await loadLexiconEntries();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_UPDATE_LEXICON')
    );
  }
};

const rollbackLexiconEntry = async entry => {
  const version = Number(entry.current_version || 0);
  if (!version) {
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_NO_PRIOR_VERSION'));
    return;
  }
  if (
    !confirmQaDestructive({
      action: 'rollback lexicon entry',
      subject: entry.phrase || `#${entry.id}`,
      detail: `Rollback to v${version} as draft (re-publish required).`,
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.rollbackSafetyLexiconEntry(entry.id, version);
    await loadLexiconEntries();
    showAlert(t('QA_DASHBOARD.SAFETY.SUCCESS_ROLLBACK_DRAFT', { version }));
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_ROLLBACK')
    );
  }
};

const deleteLexiconEntry = async entryId => {
  if (
    !confirmQaDestructive({
      action: 'delete lexicon entry',
      subject: `entry #${entryId}`,
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.deleteSafetyLexiconEntry(entryId);
    await loadLexiconEntries();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_DELETE_LEXICON')
    );
  }
};

const publishLexiconEntry = async entry => {
  if (
    !confirmQaDestructive({
      action: 'publish lexicon entry',
      subject: entry.phrase || `#${entry.id}`,
      detail: 'Publish requires second-approver policy where configured.',
    })
  ) {
    return;
  }
  try {
    await QaDashboardApi.publishSafetyLexiconEntry(entry.id);
    await loadLexiconEntries();
    showAlert(t('QA_DASHBOARD.SAFETY.SUCCESS_PUBLISHED'));
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_PUBLISH')
    );
  }
};

const incidentParams = () => {
  const params = {
    page: incidentFilters.page,
    per_page: incidentFilters.perPage,
  };
  if (incidentFilters.status !== 'all') params.status = incidentFilters.status;
  if (incidentFilters.severity !== 'all')
    params.severity = incidentFilters.severity;
  if (incidentFilters.detectorSource !== 'all') {
    params.detector_source = incidentFilters.detectorSource;
  }
  return params;
};

const loadIncidents = async () => {
  isIncidentsLoading.value = true;
  try {
    const response = await QaDashboardApi.getSafetyIncidents(incidentParams());
    incidents.value = response?.data?.items || [];
    const meta = response?.data?.meta || {};
    incidentMeta.totalCount = Number(meta.total_count || 0);
    incidentMeta.totalPages = Number(meta.total_pages || 1);
    const stats = meta.stats || {};
    incidentStats.total = Number(stats.total ?? meta.total_count ?? 0);
    incidentStats.pending = Number(stats.pending || 0);
    incidentStats.confirmed = Number(stats.confirmed || 0);
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_LOAD_INCIDENTS')
    );
  } finally {
    isIncidentsLoading.value = false;
  }
};

const loadFeedbackSummary = async () => {
  isFeedbackLoading.value = true;
  try {
    const response = await QaDashboardApi.getSafetyFeedbackSummary();
    const item = response?.data?.item || {};
    feedbackSummary.reviewedTotal = Number(item.reviewed_total || 0);
    feedbackSummary.confirmedCount = Number(item.confirmed_count || 0);
    feedbackSummary.passedCount = Number(item.passed_count || 0);
    feedbackSummary.precisionRatio = Number(item.precision_ratio || 0);
    feedbackSummary.falsePositiveRatio = Number(item.false_positive_ratio || 0);
    feedbackSummary.topFalsePositiveTerms = item.top_false_positive_terms || [];
    feedbackSummary.topConfirmedTerms = item.top_confirmed_terms || [];
    feedbackSummary.lexiconRecommendations = item.lexicon_recommendations || [];
    recommendationPreview.value = [];
    selectedPreviewIds.value = [];
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_LOAD_FEEDBACK')
    );
  } finally {
    isFeedbackLoading.value = false;
  }
};

const applyRecommendation = async row => {
  const entryId = Number(row.entry_id || 0);
  if (!entryId) return;

  isApplyingRecommendation.value = true;
  try {
    await QaDashboardApi.applySafetyLexiconRecommendation(entryId, {
      action_type: row.suggested_action || undefined,
    });
    await Promise.all([loadLexiconEntries(), loadFeedbackSummary()]);
    selectedPreviewIds.value = selectedPreviewIds.value.filter(
      id => id !== entryId
    );
    showAlert(
      t('QA_DASHBOARD.SAFETY.SUCCESS_APPLIED_REC', { phrase: row.phrase })
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_APPLY_REC')
    );
  } finally {
    isApplyingRecommendation.value = false;
  }
};

const pendingRecommendationIds = () =>
  feedbackSummary.lexiconRecommendations
    .filter(
      row => row.suggested_action && row.applied_action !== row.suggested_action
    )
    .map(row => Number(row.entry_id))
    .filter(id => id > 0);

const previewPendingRecommendations = async () => {
  const ids = pendingRecommendationIds();
  if (!ids.length) {
    recommendationPreview.value = [];
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_NO_PENDING_PREVIEW'));
    return;
  }

  isPreviewLoading.value = true;
  try {
    const response = await QaDashboardApi.previewSafetyLexiconRecommendations({
      ids,
    });
    recommendationPreview.value = response?.data?.items || [];
    selectedPreviewIds.value = recommendationPreview.value.map(row =>
      Number(row.entry_id)
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_PREVIEW_REC')
    );
  } finally {
    isPreviewLoading.value = false;
  }
};

const applySelectedRecommendations = async () => {
  const ids = selectedPreviewIds.value
    .map(item => Number(item))
    .filter(id => id > 0);
  const uniqueIds = Array.from(new Set(ids));
  if (!uniqueIds.length) {
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_NO_PREVIEW_SELECTED'));
    return;
  }

  isApplyingRecommendation.value = true;
  try {
    await QaDashboardApi.applySafetyLexiconRecommendations({ ids: uniqueIds });
    await Promise.all([loadLexiconEntries(), loadFeedbackSummary()]);
    recommendationPreview.value = [];
    selectedPreviewIds.value = [];
    showAlert(
      t('QA_DASHBOARD.SAFETY.SUCCESS_APPLIED_SELECTED', {
        count: uniqueIds.length,
      })
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_APPLY_SELECTED')
    );
  } finally {
    isApplyingRecommendation.value = false;
  }
};

const applyAllRecommendations = async () => {
  const ids = pendingRecommendationIds();
  if (!ids.length) {
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_NO_ACTIONABLE'));
    return;
  }

  isApplyingRecommendation.value = true;
  try {
    await QaDashboardApi.applySafetyLexiconRecommendations({ ids });
    await Promise.all([loadLexiconEntries(), loadFeedbackSummary()]);
    recommendationPreview.value = [];
    selectedPreviewIds.value = [];
    showAlert(
      t('QA_DASHBOARD.SAFETY.SUCCESS_APPLIED_COUNT', { count: ids.length })
    );
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.SAFETY.ERROR_APPLY_RECS')
    );
  } finally {
    isApplyingRecommendation.value = false;
  }
};

const isPreviewSelected = entryId =>
  selectedPreviewIds.value.includes(Number(entryId));

const togglePreviewSelection = (entryId, checked) => {
  const id = Number(entryId);
  if (!id) return;
  if (checked) {
    if (!selectedPreviewIds.value.includes(id)) {
      selectedPreviewIds.value = [...selectedPreviewIds.value, id];
    }
    return;
  }

  selectedPreviewIds.value = selectedPreviewIds.value.filter(
    value => value !== id
  );
};

const toggleAllPreviewSelections = checked => {
  if (!checked) {
    selectedPreviewIds.value = [];
    return;
  }

  selectedPreviewIds.value = recommendationPreview.value
    .map(row => Number(row.entry_id))
    .filter(id => id > 0);
};

const severityDelta = row => {
  const before =
    SEVERITY_RANK[String(row?.severity_before || '').toLowerCase()];
  const after = SEVERITY_RANK[String(row?.severity_after || '').toLowerCase()];
  if (Number.isInteger(before) && Number.isInteger(after)) {
    if (after > before) return 'up';
    if (after < before) return 'down';
  }
  return 'same';
};

const severityDeltaLabel = row => {
  const delta = severityDelta(row);
  if (delta === 'up') return t('QA_DASHBOARD.SAFETY.RISK_HIGHER');
  if (delta === 'down') return t('QA_DASHBOARD.SAFETY.RISK_LOWER');
  return 'No change';
};

const severityDeltaBadgeClass = row => {
  const delta = severityDelta(row);
  if (delta === 'up') return 'qa-badge qa-badge--bad';
  if (delta === 'down') return 'qa-badge qa-badge--good';
  return 'qa-badge qa-badge--muted';
};

const onIncidentFilterChange = async () => {
  incidentFilters.page = 1;
  await loadIncidents();
};

const goToIncidentPage = async page => {
  if (page < 1 || page > incidentMeta.totalPages) return;
  incidentFilters.page = page;
  await loadIncidents();
};

const isTestSentenceLoading = ref(false);
const testSentenceText = ref('');
const testSentenceResult = ref(null);

const reviewIncident = async (incident, status, options = {}) => {
  try {
    const response = await QaDashboardApi.updateSafetyIncident(incident.id, {
      status,
      // P1-SAF-018: Pass is suggest-only unless addToAllowlist is explicitly true.
      add_to_allowlist: Boolean(options.addToAllowlist) && status === 'passed',
      propose_variant:
        Boolean(options.proposeVariant) && status === 'confirmed_violation',
    });
    const updated = response?.data?.item;
    incidents.value = incidents.value.map(row =>
      row.id === incident.id ? (updated ?? row) : row
    );
    const allowlist = response?.data?.allowlist;
    if (allowlist?.added?.length) {
      showAlert(
        t('QA_DASHBOARD.SAFETY.SUCCESS_ALLOWLIST_ADDED', {
          items: allowlist.added.join(', '),
        })
      );
      await loadSafetySettings();
    } else if (allowlist?.suggested?.length && !allowlist?.applied) {
      showAlert(
        t('QA_DASHBOARD.SAFETY.SUCCESS_ALLOWLIST_SUGGESTED', {
          items: allowlist.suggested.join(', '),
        })
      );
    }
    const proposed = response?.data?.proposed_variants || [];
    if (proposed.length) {
      showAlert(
        t('QA_DASHBOARD.SAFETY.SUCCESS_PROPOSED_VARIANTS', {
          items: proposed.map(row => row.phrase).join(', '),
        })
      );
      await loadLexiconEntries();
    }
    await loadFeedbackSummary();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_UPDATE_REVIEW')
    );
  }
};

const runTestSentence = async () => {
  if (!testSentenceText.value.trim()) {
    showAlert(t('QA_DASHBOARD.SAFETY.ERROR_ENTER_SENTENCE'));
    return;
  }
  isTestSentenceLoading.value = true;
  try {
    const response = await QaDashboardApi.testSafetySentence({
      text: testSentenceText.value,
    });
    testSentenceResult.value = response?.data?.item || null;
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_TEST_SENTENCE')
    );
  } finally {
    isTestSentenceLoading.value = false;
  }
};

const exportLexicon = async () => {
  try {
    const response = await QaDashboardApi.exportSafetyLexicon();
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'qa-safety-lexicon.csv';
    link.click();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_EXPORT_LEXICON')
    );
  }
};

const importLexiconFile = async event => {
  const file = event?.target?.files?.[0];
  if (!file) return;
  try {
    const text = await file.text();
    const response = await QaDashboardApi.importSafetyLexiconCsv({ csv: text });
    showAlert(
      t('QA_DASHBOARD.SAFETY.SUCCESS_IMPORT_DONE', {
        created: response?.data?.created || 0,
        updated: response?.data?.updated || 0,
      })
    );
    await loadLexiconEntries();
  } catch (error) {
    showAlert(
      error.response?.data?.error ||
        t('QA_DASHBOARD.SAFETY.ERROR_IMPORT_LEXICON')
    );
  } finally {
    event.target.value = '';
  }
};

const conversationRoute = incident =>
  accountScopedRoute('qa_monitor_conversation', {
    conversation_id:
      incident.conversation_display_id || incident.conversation_id,
  });

const alertHubRoute = computed(() =>
  accountScopedRoute('qa_dashboard_alert_hub')
);

onMounted(async () => {
  await Promise.all([loadSafetySettings(), loadLexiconEntries()]);
  await Promise.all([loadIncidents(), loadFeedbackSummary()]);
});
</script>

<template>
  <div class="qa-page">
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.SAFETY.TITLE') }}</h1>
        </div>
        <div class="qa-header-actions">
          <RouterLink class="qa-link-quiet" :to="alertHubRoute">
            Alert Hub
          </RouterLink>
          <button
            v-if="canManagePolicies && pageSection === 'settings'"
            class="qa-btn qa-btn--primary"
            type="button"
            :disabled="isSettingsSaving || isSettingsLoading"
            @click="saveSafetySettings"
          >
            {{
              isSettingsSaving
                ? t('QA_DASHBOARD.SAFETY.BTN_SAVING')
                : t('QA_DASHBOARD.SAFETY.BTN_SAVE_SETTINGS')
            }}
          </button>
        </div>
      </div>

      <div class="qa-subheader qa-subheader--tight">
        <p class="qa-desc">
          Review abusive, sarcastic, and racist reply detections from
          multilingual lexicon matching and optional AI moderation. Configure
          detector settings and lexicon here; manage Telegram destinations and
          alert routes in Alert Hub.
        </p>
        <p v-if="seedVersion" class="qa-muted">
          Pack {{ seedVersion }} · {{ licenseSource || 'internal' }}
          <span v-if="packLedger?.version">
            · ledger {{ packLedger.version }}</span
          >
          · languages: {{ (supportedLanguages || []).join(', ') }}
        </p>
      </div>

      <div class="qa-section-nav" role="tablist" aria-label="Safety sections">
        <button
          v-for="section in pageSections"
          :key="section.id"
          type="button"
          class="qa-tab"
          :class="{ 'qa-tab--active': pageSection === section.id }"
          role="tab"
          :aria-selected="pageSection === section.id"
          @click="pageSection = section.id"
        >
          {{ section.label }}
        </button>
      </div>

      <div v-show="pageSection === 'review'" class="qa-toolbar">
        <div class="qa-toolbar-left">
          <label class="qa-field" style="min-width: 160px">
            <span class="qa-label">Status</span>
            <select
              v-model="incidentFilters.status"
              class="qa-select"
              @change="onIncidentFilterChange"
            >
              <option value="all">All</option>
              <option value="pending_review">Pending Review</option>
              <option value="confirmed_violation">Confirmed Violation</option>
              <option value="passed">Passed</option>
            </select>
          </label>
          <label class="qa-field" style="min-width: 140px">
            <span class="qa-label">Severity</span>
            <select
              v-model="incidentFilters.severity"
              class="qa-select"
              @change="onIncidentFilterChange"
            >
              <option value="all">All</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </label>
          <label class="qa-field" style="min-width: 140px">
            <span class="qa-label">Detector source</span>
            <select
              v-model="incidentFilters.detectorSource"
              class="qa-select"
              @change="onIncidentFilterChange"
            >
              <option value="all">All</option>
              <option value="keyword">Keyword</option>
              <option value="ai">AI</option>
              <option value="hybrid">Hybrid</option>
            </select>
          </label>
        </div>
        <div class="qa-toolbar-right">
          <button
            class="qa-btn"
            type="button"
            :disabled="isIncidentsLoading"
            @click="loadIncidents"
          >
            {{
              isIncidentsLoading
                ? t('QA_DASHBOARD.SAFETY.BTN_REFRESHING')
                : t('QA_DASHBOARD.SAFETY.BTN_REFRESH_INCIDENTS')
            }}
          </button>
        </div>
      </div>

      <div class="qa-content qa-content--tight">
        <div v-show="pageSection === 'review'" class="qa-kpis qa-kpis--compact">
          <div class="qa-kpi qa-kpi--total">
            <div class="qa-kpi-label">Total (filtered)</div>
            <div class="qa-kpi-value">{{ incidentStats.total }}</div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">Pending review</div>
            <div class="qa-kpi-value qa-kpi-value--warn">
              {{ incidentStats.pending }}
            </div>
          </div>
          <div class="qa-kpi">
            <div class="qa-kpi-label">Confirmed violations</div>
            <div class="qa-kpi-value qa-kpi-value--bad">
              {{ incidentStats.confirmed }}
            </div>
          </div>
        </div>

        <div v-show="pageSection === 'settings'">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"
                  />
                </svg>
                Detector settings
              </h2>
            </div>
            <div class="qa-card-body">
              <div class="qa-form-grid">
                <label class="qa-field">
                  <span class="qa-label">Safety detector enabled</span>
                  <select v-model="settingsForm.enabled" class="qa-select">
                    <option :value="true">Enabled</option>
                    <option :value="false">Disabled</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Use default multilingual lexicon</span>
                  <select
                    v-model="settingsForm.useDefaultLexicon"
                    class="qa-select"
                  >
                    <option :value="true">Enabled</option>
                    <option :value="false">Disabled</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">AI moderation enabled (async)</span>
                  <select v-model="settingsForm.aiEnabled" class="qa-select">
                    <option :value="false">Disabled</option>
                    <option :value="true">Enabled</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">AI model</span>
                  <input v-model="settingsForm.aiModel" class="qa-input" />
                </label>
                <label class="qa-field">
                  <span class="qa-label">AI minimum score</span>
                  <input
                    v-model.number="settingsForm.aiMinScore"
                    class="qa-input"
                    type="number"
                    step="0.01"
                    min="0.1"
                    max="1"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Contextual AI (candidates only)</span>
                  <select
                    v-model="settingsForm.contextualAiEnabled"
                    class="qa-select"
                  >
                    <option :value="false">Disabled</option>
                    <option :value="true">Enabled</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Contextual model</span>
                  <input
                    v-model="settingsForm.contextualAiModel"
                    class="qa-input"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Moderation daily cap</span>
                  <input
                    v-model.number="settingsForm.moderationDailyCap"
                    class="qa-input"
                    type="number"
                    min="1"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Contextual daily cap</span>
                  <input
                    v-model.number="settingsForm.contextualDailyCap"
                    class="qa-input"
                    type="number"
                    min="1"
                  />
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label"
                    >Allowlist phrases (comma separated)</span
                  >
                  <textarea
                    v-model="settingsForm.allowlistPhrasesText"
                    class="qa-textarea"
                    rows="2"
                    placeholder="order id, customer said, quoted term"
                  />
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label"
                    >Allowlist regexes (safe patterns only)</span
                  >
                  <textarea
                    v-model="settingsForm.allowlistRegexesText"
                    class="qa-textarea"
                    rows="2"
                    placeholder="customer said .*"
                  />
                  <span class="qa-hint"
                    >Telegram routes are managed in Alert Hub. Saving detector
                    settings never overwrites them.</span
                  >
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Test sentence</span>
                  <textarea
                    v-model="testSentenceText"
                    class="qa-textarea"
                    rows="2"
                    placeholder="Paste an agent reply to preview local lexicon hits"
                  />
                  <div class="qa-actions" style="margin-top: 8px">
                    <button
                      type="button"
                      class="qa-btn"
                      :disabled="isTestSentenceLoading"
                      @click="runTestSentence"
                    >
                      {{
                        isTestSentenceLoading
                          ? t('QA_DASHBOARD.SAFETY.BTN_TESTING')
                          : t('QA_DASHBOARD.SAFETY.BTN_RUN_TEST')
                      }}
                    </button>
                  </div>
                  <pre
                    v-if="testSentenceResult"
                    class="qa-hint"
                    style="margin-top: 8px; white-space: pre-wrap"
                    >{{ JSON.stringify(testSentenceResult, null, 2) }}</pre
                  >
                </label>
              </div>

              <div class="qa-subcard" style="margin: 16px 0 0">
                <div class="qa-subcard-header">
                  <div class="qa-subcard-title">Telegram routing</div>
                  <div class="qa-subcard-desc">
                    Destinations, alert routes, Telegram enablement, and
                    workforce / slow-reply thresholds are managed in Alert Hub.
                    Saving detector settings here preserves the existing
                    Telegram configuration.
                  </div>
                  <div class="qa-actions" style="margin-top: 10px">
                    <RouterLink class="qa-btn qa-btn--sm" :to="alertHubRoute">
                      Configure in Alert Hub
                    </RouterLink>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
        <div v-show="pageSection === 'quality'">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 19V5m4 14V9m4 10V5m4 14v-6m4 6V8"
                  />
                </svg>
                Feedback loop
              </h2>
              <button
                class="qa-btn qa-btn--sm"
                type="button"
                :disabled="isFeedbackLoading"
                @click="loadFeedbackSummary"
              >
                Refresh
              </button>
            </div>
            <div class="qa-card-body">
              <div class="qa-kpis">
                <div class="qa-kpi">
                  <div class="qa-kpi-label">Reviewed incidents</div>
                  <div class="qa-kpi-value">
                    {{ feedbackSummary.reviewedTotal }}
                  </div>
                </div>
                <div class="qa-kpi">
                  <div class="qa-kpi-label">Precision ratio</div>
                  <div class="qa-kpi-value qa-kpi-value--good">
                    {{ (feedbackSummary.precisionRatio * 100).toFixed(1) }}%
                  </div>
                </div>
                <div class="qa-kpi">
                  <div class="qa-kpi-label">False positive ratio</div>
                  <div class="qa-kpi-value qa-kpi-value--bad">
                    {{ (feedbackSummary.falsePositiveRatio * 100).toFixed(1) }}%
                  </div>
                </div>
              </div>

              <div
                class="qa-form-grid qa-form-grid--2"
                style="margin-top: 16px"
              >
                <div class="qa-subcard" style="margin: 0">
                  <div class="qa-subcard-header">
                    <div class="qa-subcard-title">Top false-positive terms</div>
                  </div>
                  <div class="qa-card-body">
                    <ul class="qa-term-list">
                      <li
                        v-for="item in feedbackSummary.topFalsePositiveTerms"
                        :key="`fp-${item.term}`"
                      >
                        {{ item.term }} ({{ item.count }})
                      </li>
                      <li
                        v-if="!feedbackSummary.topFalsePositiveTerms.length"
                        class="qa-muted"
                      >
                        -
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="qa-subcard" style="margin: 0">
                  <div class="qa-subcard-header">
                    <div class="qa-subcard-title">Top confirmed terms</div>
                  </div>
                  <div class="qa-card-body">
                    <ul class="qa-term-list">
                      <li
                        v-for="item in feedbackSummary.topConfirmedTerms"
                        :key="`ok-${item.term}`"
                      >
                        {{ item.term }} ({{ item.count }})
                      </li>
                      <li
                        v-if="!feedbackSummary.topConfirmedTerms.length"
                        class="qa-muted"
                      >
                        -
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="qa-divider" />

              <div
                class="qa-actions"
                style="justify-content: space-between; margin-bottom: 12px"
              >
                <!-- prettier-ignore -->
                <h3 class="qa-card-title" style="font-size: 14px">Lexicon recommendations</h3>
                <div class="qa-actions">
                  <button
                    class="qa-btn qa-btn--sm"
                    type="button"
                    :disabled="isPreviewLoading || isFeedbackLoading"
                    @click="previewPendingRecommendations"
                  >
                    {{
                      isPreviewLoading
                        ? t('QA_DASHBOARD.SAFETY.BTN_PREVIEWING')
                        : t('QA_DASHBOARD.SAFETY.BTN_PREVIEW_PENDING')
                    }}
                  </button>
                  <button
                    class="qa-btn qa-btn--sm"
                    type="button"
                    :disabled="
                      isApplyingRecommendation ||
                      isFeedbackLoading ||
                      !selectedPreviewIds.length
                    "
                    @click="applySelectedRecommendations"
                  >
                    Apply selected ({{ selectedPreviewIds.length }})
                  </button>
                  <button
                    class="qa-btn qa-btn--primary qa-btn--sm"
                    type="button"
                    :disabled="isApplyingRecommendation || isFeedbackLoading"
                    @click="applyAllRecommendations"
                  >
                    {{
                      isApplyingRecommendation
                        ? t('QA_DASHBOARD.SAFETY.BTN_APPLYING')
                        : t('QA_DASHBOARD.SAFETY.BTN_APPLY_ALL')
                    }}
                  </button>
                </div>
              </div>

              <div class="qa-table-wrap">
                <table class="qa-table">
                  <thead>
                    <tr>
                      <th class="qa-th">
                        <input
                          type="checkbox"
                          :checked="
                            recommendationPreview.length > 0 &&
                            selectedPreviewIds.length ===
                              recommendationPreview.length
                          "
                          @change="
                            toggleAllPreviewSelections($event.target.checked)
                          "
                        />
                      </th>
                      <th class="qa-th">Preview phrase</th>
                      <th class="qa-th">Action</th>
                      <th class="qa-th">Enabled</th>
                      <th class="qa-th">Severity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in recommendationPreview"
                      :key="`preview-${row.entry_id}`"
                    >
                      <td class="qa-td">
                        <input
                          type="checkbox"
                          :checked="isPreviewSelected(row.entry_id)"
                          @change="
                            togglePreviewSelection(
                              row.entry_id,
                              $event.target.checked
                            )
                          "
                        />
                      </td>
                      <td class="qa-td">{{ row.phrase }}</td>
                      <td class="qa-td">{{ row.action }}</td>
                      <td class="qa-td">
                        {{ row.enabled_before ? 'true' : 'false' }} →
                        {{ row.enabled_after ? 'true' : 'false' }}
                      </td>
                      <td class="qa-td">
                        {{ row.severity_before }} → {{ row.severity_after }}
                        <span
                          :class="severityDeltaBadgeClass(row)"
                          style="margin-left: 8px"
                        >
                          {{ severityDeltaLabel(row) }}
                        </span>
                      </td>
                    </tr>
                    <tr v-if="!recommendationPreview.length">
                      <td class="qa-td qa-td--empty" colspan="5">
                        No preview loaded. Click "Preview pending" first.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="qa-table-wrap" style="margin-top: 16px">
                <table class="qa-table">
                  <thead>
                    <tr>
                      <th class="qa-th">Lexicon phrase</th>
                      <th class="qa-th">Reviewed</th>
                      <th class="qa-th">Confirmed</th>
                      <th class="qa-th">Passed</th>
                      <th class="qa-th">Recommendation</th>
                      <th class="qa-th">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in feedbackSummary.lexiconRecommendations"
                      :key="row.entry_id"
                    >
                      <td class="qa-td">{{ row.phrase }}</td>
                      <td class="qa-td">{{ row.reviewed_total }}</td>
                      <td class="qa-td">{{ row.confirmed_count }}</td>
                      <td class="qa-td">{{ row.passed_count }}</td>
                      <td class="qa-td">
                        <div>{{ row.recommendation || '-' }}</div>
                        <div v-if="row.applied_action" class="qa-subtle">
                          Applied: {{ row.applied_action }}
                        </div>
                      </td>
                      <td class="qa-td">
                        <button
                          class="qa-btn qa-btn--sm"
                          type="button"
                          :disabled="
                            isApplyingRecommendation ||
                            !row.suggested_action ||
                            row.applied_action === row.suggested_action
                          "
                          @click="applyRecommendation(row)"
                        >
                          {{
                            row.suggested_action
                              ? `Apply ${row.suggested_action}`
                              : 'N/A'
                          }}
                        </button>
                      </td>
                    </tr>
                    <tr v-if="!feedbackSummary.lexiconRecommendations.length">
                      <td class="qa-td qa-td--empty" colspan="6">
                        No feedback recommendations yet.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </div>
        <div v-show="pageSection === 'lexicon'">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4 6h16M4 12h10M4 18h7"
                  />
                </svg>
                Custom lexicon
              </h2>
              <div
                v-if="canManagePolicies"
                class="qa-actions qa-actions--compact"
              >
                <button
                  class="qa-btn qa-btn--sm"
                  type="button"
                  :disabled="isLexiconLoading"
                  @click="loadLexiconEntries"
                >
                  Refresh
                </button>
                <DropdownContainer teleport>
                  <template #trigger="{ toggle }">
                    <button
                      class="qa-btn qa-btn--sm"
                      type="button"
                      @click="toggle"
                    >
                      Import / Export
                    </button>
                  </template>
                  <div class="qa-more-menu">
                    <button
                      class="qa-btn qa-btn--sm"
                      type="button"
                      @click="exportLexicon"
                    >
                      Export CSV
                    </button>
                    <label
                      class="qa-btn qa-btn--sm"
                      style="cursor: pointer; margin: 0"
                    >
                      Import CSV
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        style="display: none"
                        @change="importLexiconFile"
                      />
                    </label>
                  </div>
                </DropdownContainer>
              </div>
            </div>
            <div class="qa-card-body">
              <div
                class="qa-form-grid"
                style="align-items: end; margin-bottom: 12px"
              >
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Search</span>
                  <input
                    v-model="lexiconFilters.q"
                    class="qa-input"
                    placeholder="phrase or note"
                    @keyup.enter="loadLexiconEntries"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Language</span>
                  <select v-model="lexiconFilters.language" class="qa-select">
                    <option value="all">All</option>
                    <option value="ms">Malay</option>
                    <option value="zh">Chinese</option>
                    <option value="en">English</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Category</span>
                  <select v-model="lexiconFilters.category" class="qa-select">
                    <option value="all">All</option>
                    <option
                      v-for="category in taxonomyOptions"
                      :key="`f-${category}`"
                      :value="category"
                    >
                      {{ category }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Lifecycle</span>
                  <select
                    v-model="lexiconFilters.lifecycle_status"
                    class="qa-select"
                  >
                    <option value="all">All</option>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Scope</span>
                  <select v-model="lexiconFilters.scope" class="qa-select">
                    <option value="all">All</option>
                    <option value="custom">Custom</option>
                    <option value="proposals">Proposals</option>
                  </select>
                </label>
                <div class="qa-field">
                  <span class="qa-label">&nbsp;</span>
                  <button
                    class="qa-btn qa-btn--sm"
                    type="button"
                    @click="loadLexiconEntries"
                  >
                    Filter
                  </button>
                </div>
              </div>

              <div class="qa-form-grid" style="align-items: end">
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Phrase</span>
                  <input v-model="newEntry.phrase" class="qa-input" />
                </label>
                <label class="qa-field qa-field--span-2">
                  <span class="qa-label">Note</span>
                  <input
                    v-model="newEntry.note"
                    class="qa-input"
                    placeholder="source / reviewer note"
                  />
                </label>
                <label class="qa-field">
                  <span class="qa-label">Language</span>
                  <select v-model="newEntry.language" class="qa-select">
                    <option value="ms">Malay</option>
                    <option value="zh">Chinese</option>
                    <option value="en">English</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Severity</span>
                  <select v-model="newEntry.severity" class="qa-select">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="critical">Critical</option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Category</span>
                  <select v-model="newEntry.category" class="qa-select">
                    <option
                      v-for="category in taxonomyOptions"
                      :key="category"
                      :value="category"
                    >
                      {{ category }}
                    </option>
                  </select>
                </label>
                <label class="qa-field">
                  <span class="qa-label">Match type</span>
                  <select v-model="newEntry.matchType" class="qa-select">
                    <option value="contains">Contains</option>
                    <option value="exact">Exact</option>
                    <option value="regex">Regex</option>
                  </select>
                </label>
                <div class="qa-field">
                  <span class="qa-label">&nbsp;</span>
                  <button
                    class="qa-btn qa-btn--primary"
                    type="button"
                    @click="createLexiconEntry"
                  >
                    Add draft entry
                  </button>
                </div>
              </div>

              <div class="qa-table-wrap" style="margin-top: 16px">
                <table class="qa-table">
                  <thead>
                    <tr>
                      <th class="qa-th">Phrase</th>
                      <th class="qa-th">Note</th>
                      <th class="qa-th">Language</th>
                      <th class="qa-th">Severity</th>
                      <th class="qa-th">Category</th>
                      <th class="qa-th">Match</th>
                      <th class="qa-th">Enabled</th>
                      <th class="qa-th">Status</th>
                      <th class="qa-th">Ver</th>
                      <th class="qa-th">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="entry in lexiconEntries" :key="entry.id">
                      <td class="qa-td">
                        <input
                          v-model="entry.phrase"
                          class="qa-input qa-inline-input"
                        />
                        <div
                          v-if="entry.proposed_from_incident_id"
                          class="qa-muted"
                        >
                          proposal #{{ entry.proposed_from_incident_id }}
                        </div>
                      </td>
                      <td class="qa-td">
                        <input
                          v-model="entry.note"
                          class="qa-input qa-inline-input"
                        />
                      </td>
                      <td class="qa-td">
                        <select
                          v-model="entry.language"
                          class="qa-select qa-inline-input"
                        >
                          <option value="ms">Malay</option>
                          <option value="zh">Chinese</option>
                          <option value="en">English</option>
                          <option value="mixed">Mixed</option>
                        </select>
                      </td>
                      <td class="qa-td">
                        <select
                          v-model="entry.severity"
                          class="qa-select qa-inline-input"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="critical">Critical</option>
                        </select>
                      </td>
                      <td class="qa-td">
                        <input
                          v-model="entry.category"
                          class="qa-input qa-inline-input"
                        />
                      </td>
                      <td class="qa-td">
                        <select
                          v-model="entry.match_type"
                          class="qa-select qa-inline-input"
                        >
                          <option value="contains">Contains</option>
                          <option value="exact">Exact</option>
                          <option value="regex">Regex</option>
                        </select>
                      </td>
                      <td class="qa-td">
                        <input
                          v-model="entry.enabled"
                          type="checkbox"
                          :disabled="entry.lifecycle_status !== 'published'"
                        />
                      </td>
                      <td class="qa-td">
                        <span class="qa-muted">{{
                          entry.lifecycle_status || 'draft'
                        }}</span>
                      </td>
                      <td class="qa-td">
                        <span class="qa-muted"
                          >v{{ entry.current_version || 0 }}</span
                        >
                      </td>
                      <td class="qa-td">
                        <div class="qa-actions qa-actions--compact">
                          <button
                            class="qa-btn qa-btn--primary qa-btn--sm"
                            type="button"
                            @click="updateLexiconEntry(entry)"
                          >
                            Save
                          </button>
                          <DropdownContainer teleport>
                            <template #trigger="{ toggle }">
                              <button
                                class="qa-btn qa-btn--sm"
                                type="button"
                                @click="toggle"
                              >
                                More
                              </button>
                            </template>
                            <div class="qa-more-menu">
                              <button
                                v-if="entry.lifecycle_status === 'draft'"
                                class="qa-btn qa-btn--sm"
                                type="button"
                                @click="publishLexiconEntry(entry)"
                              >
                                Publish
                              </button>
                              <button
                                v-if="entry.current_version > 0"
                                class="qa-btn qa-btn--sm"
                                type="button"
                                @click="rollbackLexiconEntry(entry)"
                              >
                                Rollback
                              </button>
                              <button
                                class="qa-btn qa-btn--danger qa-btn--sm"
                                type="button"
                                @click="deleteLexiconEntry(entry.id)"
                              >
                                Delete
                              </button>
                            </div>
                          </DropdownContainer>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!lexiconEntries.length">
                      <td class="qa-td qa-td--empty" colspan="10">
                        No custom entries.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <details class="qa-details">
                <summary>Built-in default lexicon preview</summary>
                <div class="qa-table-wrap">
                  <table class="qa-table">
                    <thead>
                      <tr>
                        <th class="qa-th">Phrase</th>
                        <th class="qa-th">Language</th>
                        <th class="qa-th">Severity</th>
                        <th class="qa-th">Category</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="entry in defaultLexicon" :key="entry.key">
                        <td class="qa-td">{{ entry.phrase }}</td>
                        <td class="qa-td">{{ entry.language }}</td>
                        <td class="qa-td">{{ entry.severity }}</td>
                        <td class="qa-td">{{ entry.category }}</td>
                      </tr>
                      <tr v-if="!defaultLexicon.length">
                        <td class="qa-td qa-td--empty" colspan="4">
                          No default lexicon entries.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          </section>
        </div>
        <div v-show="pageSection === 'review'">
          <section class="qa-card">
            <div class="qa-card-header">
              <h2 class="qa-card-title">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6M7 4h10a2 2 0 0 1 2 2v14l-4-2-4 2-4-2-4 2V6a2 2 0 0 1 2-2Z"
                  />
                </svg>
                Incidents
              </h2>
            </div>
            <div class="qa-table-wrap">
              <table class="qa-table">
                <thead>
                  <tr>
                    <th class="qa-th">Conversation</th>
                    <th class="qa-th">Status</th>
                    <th class="qa-th">Severity</th>
                    <th class="qa-th">Source</th>
                    <th class="qa-th">Matched terms</th>
                    <th class="qa-th">Excerpt</th>
                    <th class="qa-th">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="isIncidentsLoading">
                    <td class="qa-td qa-td--empty" colspan="7">
                      <div class="qa-loading-row">
                        <div class="qa-spinner" />
                        <span>Loading...</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-for="incident in incidents" :key="incident.id">
                    <td class="qa-td">
                      <RouterLink
                        :to="conversationRoute(incident)"
                        class="qa-link"
                      >
                        #{{
                          incident.conversation_display_id ||
                          incident.conversation_id
                        }}
                      </RouterLink>
                    </td>
                    <td class="qa-td">
                      <span class="qa-badge">{{ incident.status }}</span>
                    </td>
                    <td class="qa-td">
                      <span
                        class="qa-badge"
                        :class="{
                          'qa-badge--bad':
                            incident.severity === 'critical' ||
                            incident.severity === 'high',
                          'qa-badge--warn': incident.severity === 'medium',
                          'qa-badge--muted': incident.severity === 'low',
                        }"
                      >
                        {{ incident.severity }}
                      </span>
                    </td>
                    <td class="qa-td">
                      <span class="qa-badge qa-badge--info">
                        {{ incident.detector_source }}
                      </span>
                    </td>
                    <td class="qa-td">
                      {{ (incident.matched_terms || []).join(', ') || '-' }}
                    </td>
                    <td class="qa-td qa-td--pre">
                      {{ incident.message_excerpt || '-' }}
                    </td>
                    <td class="qa-td">
                      <div
                        v-if="canReview"
                        class="qa-actions qa-actions--compact"
                      >
                        <button
                          class="qa-btn qa-btn--sm"
                          type="button"
                          @click="reviewIncident(incident, 'passed')"
                        >
                          Pass
                        </button>
                        <button
                          class="qa-btn qa-btn--primary qa-btn--sm"
                          type="button"
                          @click="
                            reviewIncident(incident, 'confirmed_violation')
                          "
                        >
                          Confirm
                        </button>
                        <DropdownContainer teleport>
                          <template #trigger="{ toggle }">
                            <button
                              class="qa-btn qa-btn--sm"
                              type="button"
                              @click="toggle"
                            >
                              More
                            </button>
                          </template>
                          <div class="qa-more-menu">
                            <button
                              class="qa-btn qa-btn--sm"
                              type="button"
                              @click="
                                reviewIncident(incident, 'passed', {
                                  addToAllowlist: true,
                                })
                              "
                            >
                              Pass + allowlist
                            </button>
                            <button
                              class="qa-btn qa-btn--sm"
                              type="button"
                              @click="
                                reviewIncident(
                                  incident,
                                  'confirmed_violation',
                                  { proposeVariant: true }
                                )
                              "
                            >
                              Confirm + propose
                            </button>
                          </div>
                        </DropdownContainer>
                      </div>
                      <span v-else class="qa-muted">—</span>
                    </td>
                  </tr>
                  <tr v-if="!incidents.length && !isIncidentsLoading">
                    <!-- prettier-ignore -->
                    <td class="qa-td qa-td--empty" colspan="7">No incidents found.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="qa-pagination">
              <button
                class="qa-btn qa-btn--sm"
                type="button"
                :disabled="incidentFilters.page <= 1"
                @click="goToIncidentPage(incidentFilters.page - 1)"
              >
                Prev
              </button>
              <span class="qa-hint">
                Page {{ incidentFilters.page }} /
                {{ incidentMeta.totalPages }} ({{ incidentMeta.totalCount }}
                total)
              </span>
              <button
                class="qa-btn qa-btn--sm"
                type="button"
                :disabled="incidentFilters.page >= incidentMeta.totalPages"
                @click="goToIncidentPage(incidentFilters.page + 1)"
              >
                Next
              </button>
            </div>
          </section>
        </div>
      </div>
      <div class="qa-footer qa-footer--hidden">
        <span>
          Safety Audit — lexicon and AI detection review. Telegram routing lives
          in Alert Hub.
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '../styles/qaShell.css';

.qa-term-list {
  margin: 0;
  padding-left: 18px;
  font-size: 13px;
  color: var(--qa-text-secondary, #374151);
}
</style>
